import { ECreditApplicationStep, EDocumentStatus, EDocumentType } from '@if/core/types'
import { formatDateTime } from '@if/core/utils'
import { useMemo } from 'react'
import { useDocumentTypes } from './app-config-selectors'
import { Document, DocumentDecision, DocumentRequired } from '../types'
import { DocumentPageDisplayInfo } from '../types/DocumentPageDisplayInfo'
import { DocumentPage } from '../types/DocumentSchema'

function allDocumentRequiredForStep(step: ECreditApplicationStep, docs: Document[], docsReq: DocumentRequired[]) {
  return docsReq.every(
    (x) =>
      x.requiredBeforeStep !== step ||
      docs.some(
        (y) =>
          y.applicantType === x.applicantType &&
          y.typeId === x.typeId &&
          y.status === EDocumentStatus.Approved &&
          y.subKey === x.subKey,
      ),
  )
}

function getPrioritizedDocumentStatus(documents: Document[]): EDocumentStatus | undefined {
  if (documents.length === 0) return undefined
  if (documents.some((d) => d.status === EDocumentStatus.AwaitingScan)) return EDocumentStatus.AwaitingScan
  if (documents.some((d) => d.status === EDocumentStatus.Approved)) return EDocumentStatus.Approved
  const incompleteDoc = documents.find((d) => d.status === EDocumentStatus.Incomplete)
  if (incompleteDoc) {
    const awaitingApprovalDocs = documents.filter((d) => d.status === EDocumentStatus.AwaitingApproval)
    if (
      awaitingApprovalDocs.some((d) => d.updatedOn && incompleteDoc.updatedOn && d.updatedOn > incompleteDoc.updatedOn)
    )
      return EDocumentStatus.AwaitingApproval
    return EDocumentStatus.Incomplete
  }
  if (documents.some((d) => d.status === EDocumentStatus.AwaitingApproval)) return EDocumentStatus.AwaitingApproval
  if (documents.some((d) => d.status === EDocumentStatus.Refused)) return EDocumentStatus.Refused
  if (documents.some((d) => d.status === EDocumentStatus.Deleted)) return EDocumentStatus.Deleted
  return EDocumentStatus.AwaitingApproval
}

const statusOrder = {
  [EDocumentStatus.AwaitingScan]: 1,
  [EDocumentStatus.AwaitingDocument]: 2,
  [EDocumentStatus.AwaitingApproval]: 3,
  [EDocumentStatus.Incomplete]: 4,
  [EDocumentStatus.Refused]: 5,
  [EDocumentStatus.Approved]: 6,
  [EDocumentStatus.Deleted]: 7,
}

/**
 * Hook to check if all documents required for a specific step are approved
 */
export function useAreAllDocumentsRequiredForStep(
  step: ECreditApplicationStep,
  documents: Document[],
  requiredDocuments: DocumentRequired[],
) {
  return useMemo(() => {
    return allDocumentRequiredForStep(step, documents, requiredDocuments)
  }, [step, documents, requiredDocuments])
}

/**
 * Hook to check if all documents required for credit step are approved
 */
export function useAreAllDocumentsRequiredForCredit(documents: Document[], requiredDocuments: DocumentRequired[]) {
  return useAreAllDocumentsRequiredForStep(ECreditApplicationStep.Credit, documents, requiredDocuments)
}

/**
 * Hook to check if all documents required for financing step are approved
 */
export function useAreAllDocumentsRequiredForFinancing(documents: Document[], requiredDocuments: DocumentRequired[]) {
  return useAreAllDocumentsRequiredForStep(ECreditApplicationStep.Financing, documents, requiredDocuments)
}

/**
 * Hook to check if all documents required for CVT step are approved
 */
export function useAreAllDocumentsRequiredForCVT(documents: Document[], requiredDocuments: DocumentRequired[]) {
  return useMemo(() => {
    return (
      allDocumentRequiredForStep(ECreditApplicationStep.CVT, documents, requiredDocuments) &&
      allDocumentRequiredForStep(ECreditApplicationStep.Financing, documents, requiredDocuments) &&
      allDocumentRequiredForStep(ECreditApplicationStep.Credit, documents, requiredDocuments)
    )
  }, [documents, requiredDocuments])
}

/**
 * Hook to check if all documents required for payout step are approved
 */
export function useAreAllDocumentsRequiredForPayOut(documents: Document[], requiredDocuments: DocumentRequired[]) {
  return useMemo(() => {
    return (
      allDocumentRequiredForStep(ECreditApplicationStep.Financing, documents, requiredDocuments) &&
      allDocumentRequiredForStep(ECreditApplicationStep.Credit, documents, requiredDocuments) &&
      allDocumentRequiredForStep(ECreditApplicationStep.CVT, documents, requiredDocuments) &&
      allDocumentRequiredForStep(ECreditApplicationStep.PayOut, documents, requiredDocuments)
    )
  }, [documents, requiredDocuments])
}

/**
 * Hook to check if there are documents pending approval
 */
export function useHasDocumentsPendingApproval(documents: Document[]) {
  return useMemo(() => {
    return documents.some((x) => x.status === EDocumentStatus.AwaitingScan || x.status === EDocumentStatus.AwaitingApproval)
  }, [documents])
}

/**
 * Hook to check if all required documents are approved
 */
export function useAreAllDocumentsRequiredApproved(documents: Document[], requiredDocuments: DocumentRequired[]) {
  return useMemo(() => {
    return requiredDocuments.every((x) =>
      documents.some(
        (y) =>
          y.applicantType === x.applicantType &&
          y.typeId === x.typeId &&
          y.status === EDocumentStatus.Approved &&
          y.subKey === x.subKey,
      ),
    )
  }, [documents, requiredDocuments])
}

/**
 * Hook to get document page display info for the split docs page
 */
export function useDocumentPageDisplayInfos(documents: Document[], documentPages: DocumentPage[]) {
  return useMemo(() => {
    const ret: DocumentPageDisplayInfo[] = []
    if (documents.length > 0) {
      documents.forEach((d) => {
        const concernedPages = documentPages.filter((p) => p.documentId === d.id)
        concernedPages.forEach((p) => {
          const documentPageDisplayInfo: DocumentPageDisplayInfo = {
            id: p.id,
            documentId: p.documentId!,
            src: p.thumbnailUrl,
            rotation: p.rotation,
            pageNumber: p.pageNumber,
            originalFilename: d.fileName!,
            documentType: d.typeId,
            documentStatus: d.status!,
            pageStatus: p.status!,
            applicantType: d.applicantType!,
            subKey: d.subKey,
          }
          ret.push(documentPageDisplayInfo)
        })
      })
    }
    return ret
  }, [documents, documentPages])
}

/**
 * Hook to get document decisions for the decision dialog
 */
export function useDocumentDecisions(documents: Document[], documentIds: string[]) {
  return useMemo(() => {
    const decisions: DocumentDecision[] = []
    documents
      .filter((d) => documentIds.includes(d.id!))
      .forEach((d) => {
        if (d.typeId !== EDocumentType.VehicleValueEvaluation) {
          const documentDecision: DocumentDecision = {
            documentId: d.id!,
            documentType: d.typeId,
            status: d.status!,
            refusalReason: d.refusalReason ?? null,
            updatedBy: d.updatedByUserFullname ?? '',
            lastUpdate: d.updatedOn ? formatDateTime(d.updatedOn) : '',
            applicantType: d.applicantType ?? null,
            versionTag: d.versionTag!,
            subKey: d.subKey,
          }
          decisions.push(documentDecision)
        }
      })

    let ordered = decisions.filter((d) => d.status === EDocumentStatus.AwaitingApproval)
    ordered = ordered.concat(decisions.filter((d) => d.status !== EDocumentStatus.AwaitingApproval))

    const decisionIds = decisions.map((d) => d.documentId)
    if (documentIds?.length === 1 && decisionIds.includes(documentIds[0])) {
      const concernedDecision = decisions.find((d) => d.documentId === documentIds[0])
      const decisionsWithoutWantedOne = decisions.filter((d) => d.documentId !== documentIds[0])

      decisionsWithoutWantedOne.unshift(concernedDecision!)
      ordered = decisionsWithoutWantedOne
    }
    return ordered
  }, [documents, documentIds])
}

/**
 * Hook to get multiple required documents (documents that can have multiple instances)
 */
export function useMultipleRequiredDocuments(requiredDocuments: DocumentRequired[]) {
  const documentTypes = useDocumentTypes()

  return useMemo(() => {
    const possibleMultipleTypes = Object.values(documentTypes)
      .filter((dt) => dt.canHaveMultiple)
      .map((dt) => dt.id)

    const requiredDocsThatAreInMultipleTypes = requiredDocuments.filter((rd) =>
      possibleMultipleTypes.includes(rd.typeId),
    )
    return requiredDocsThatAreInMultipleTypes
  }, [requiredDocuments, documentTypes])
}

/**
 * Hook to check if able to send fund deposit confirmation
 */
export function useIsAbleToSendFundDepositConfirmation(documents: Document[]) {
  return useMemo(() => {
    const signedCVTApproved = documents.some(
      (x) => x.typeId === EDocumentType.SignedCVT && x.status === EDocumentStatus.Approved,
    )

    if (signedCVTApproved) {
      const auditCVTToValidate = documents.find((x) => x.typeId === EDocumentType.AuditCVT)
      if (
        !auditCVTToValidate ||
        auditCVTToValidate.status === EDocumentStatus.Approved ||
        auditCVTToValidate.status === EDocumentStatus.Incomplete ||
        auditCVTToValidate.status === EDocumentStatus.Refused
      )
        return true
    }
    return false
  }, [documents])
}

/**
 * Hook to get required document status from matching documents
 */
export function useRequiredDocumentStatusFromMatchingDocuments(
  documents: Document[],
  requiredDocuments: DocumentRequired[],
) {
  return useMemo(() => {
    const requiredDocumentStatuses: Record<string, EDocumentStatus | undefined> = {}
    requiredDocuments.forEach((r) => {
      const concernedDocs = documents.filter(
        (d) => d.typeId === r.typeId && d.applicantType === r.applicantType && d.subKey === r.subKey,
      )
      requiredDocumentStatuses[String(r.id)] = getPrioritizedDocumentStatus(concernedDocs)
    })

    return requiredDocumentStatuses
  }, [documents, requiredDocuments])
}

/**
 * Hook to get documents sorted by status
 */
export function useSortedDocumentsByStatus(documents: Document[]) {
  return useMemo(() => {
    return [...documents].sort((a, b) => {
      const statusA = a.status ? statusOrder[a.status] || 0 : 0
      const statusB = b.status ? statusOrder[b.status] || 0 : 0

      return statusA - statusB
    })
  }, [documents])
}

/**
 * Hook to get required documents sorted by status
 */
export function useSortedRequiredDocumentsByStatus(documents: Document[], requiredDocuments: DocumentRequired[]) {
  return useMemo(() => {
    const requiredDocumentStatuses: Record<string, EDocumentStatus> = {}

    requiredDocuments.forEach((reqDoc) => {
      const docsForThisRequest = documents.filter(
        (doc) =>
          doc.typeId === reqDoc.typeId && doc.applicantType === reqDoc.applicantType && doc.subKey === reqDoc.subKey,
      )
      requiredDocumentStatuses[String(reqDoc.id)] =
        getPrioritizedDocumentStatus(docsForThisRequest) ?? EDocumentStatus.AwaitingDocument
    })

    return [...requiredDocuments].sort((a, b) => {
      const aStatus = requiredDocumentStatuses[String(a.id)]
      const bStatus = requiredDocumentStatuses[String(b.id)]
      const statusA = statusOrder[aStatus]
      const statusB = statusOrder[bStatus]

      return statusA - statusB
    })
  }, [documents, requiredDocuments])
}

/**
 * Hook to check if CVT document has been received
 */
export function useHasReceivedCvtDocument(documents: Document[]) {
  return useMemo(() => {
    return documents.some(
      (x) =>
        x.typeId === EDocumentType.SignedCVT &&
        (x.status === EDocumentStatus.AwaitingApproval || x.status === EDocumentStatus.Approved),
    )
  }, [documents])
}

/**
 * Hook to check if all required documents for Credit or Financing steps are satisfied
 */
export function useAreRequiredDocumentsForCreditOrFinancingSatisfied(
  documents: Document[],
  requiredDocuments: DocumentRequired[],
) {
  return useMemo(() => {
    return !requiredDocuments.some(
      (x) =>
        !documents.some(
          (y) =>
            y.status === EDocumentStatus.Approved && x.typeId === y.typeId && x.applicantType === y.applicantType,
        ) &&
        (x.requiredBeforeStep === ECreditApplicationStep.Credit ||
          x.requiredBeforeStep === ECreditApplicationStep.Financing),
    )
  }, [documents, requiredDocuments])
}
